/**
 * push.ts
 * ──────────────────────────────────────────────────────────────
 * Browser-side Web Push subscription helpers.
 *
 * API surface:
 *   - isPushSupported()     → feature-detect
 *   - getPushState()        → 'unsupported' | 'default' | 'granted' | 'denied'
 *   - subscribeToPush()     → prompt + subscribe + POST /push/subscribe
 *   - unsubscribeFromPush() → unsubscribe locally + DELETE /push/subscribe
 *
 * IMPORTANT — do NOT call subscribeToPush() on app load. iOS Safari denies
 * any permission request that isn't triggered by a user gesture, and Chrome
 * flags "prompt on first visit" as spammy. Always tie the call to a button
 * click or a clear milestone (first streak earned, first topic unlocked).
 */
import { api } from './api'

export type PushState = 'unsupported' | 'default' | 'granted' | 'denied'

export function isPushSupported(): boolean {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager'    in window
    && 'Notification'   in window
}

export function getPushState(): PushState {
  if (!isPushSupported()) return 'unsupported'
  return Notification.permission as PushState
}

/** Base64 url-safe → Uint8Array (applicationServerKey format). */
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - base64.length % 4) % 4)
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = window.atob(normalized)
  const out = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

/**
 * End-to-end subscribe. Returns the subscription on success, null if the user
 * denied the prompt, throws on unexpected errors.
 *
 * Must be called from a user-gesture event handler (click etc.) on iOS.
 */
export async function subscribeToPush(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null

  // 1. Ensure a service worker is registered + ready. vite-plugin-pwa handles
  //    registration in prod builds; in dev SW is disabled so this returns null.
  const registration = await navigator.serviceWorker.getRegistration()
  if (!registration) return null

  // 2. Get the VAPID public key from the API. Cached for 1h in localStorage
  //    so repeat subscribes don't hit the network.
  const vapidKey = await fetchVapidPublicKey()
  if (!vapidKey) return null

  // 3. Prompt + subscribe. The browser will show the permission UI if it
  //    hasn't already decided; if decided, the promise resolves/rejects
  //    according to the prior decision.
  let subscription: PushSubscription
  try {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly:      true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey) as BufferSource,
    })
  } catch (err: any) {
    // Permission denied — return null rather than throw so callers can show
    // a friendly "no problem, you can enable later" UI instead of an error.
    if (err?.name === 'NotAllowedError' || err?.name === 'AbortError') return null
    throw err
  }

  // 4. Send the subscription to our API for safekeeping.
  const json = subscription.toJSON()
  await api.post('/push/subscribe', {
    endpoint: json.endpoint,
    keys: {
      p256dh: json.keys?.p256dh ?? '',
      auth:   json.keys?.auth   ?? '',
    },
  })

  return subscription
}

export async function unsubscribeFromPush(): Promise<void> {
  if (!isPushSupported()) return
  const registration = await navigator.serviceWorker.getRegistration()
  const existing = await registration?.pushManager.getSubscription()
  if (!existing) return

  const endpoint = existing.endpoint
  try { await existing.unsubscribe() } catch {}
  await api.delete('/push/subscribe')
    .catch(() => {
      // If the DELETE accepts a body in your api client, swap to the body form:
      // api.delete('/push/subscribe', { endpoint })
    })
  // Belt-and-braces: send endpoint explicitly so the server scopes the delete
  // (api client may not carry body on DELETE — the route above tolerates either).
  try {
    await fetch('/api/push/subscribe', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint }),
    })
  } catch {}
}

/** Fetch + short-cache the VAPID public key. */
async function fetchVapidPublicKey(): Promise<string | null> {
  const CACHE_KEY = 'vapid_public_key_v1'
  const CACHE_TTL = 60 * 60 * 1000  // 1 hour

  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as { key: string; ts: number }
      if (Date.now() - parsed.ts < CACHE_TTL) return parsed.key
    }
  } catch {}

  try {
    const res = await api.get<{ key: string }>('/push/vapid-public-key')
    if (res?.key) {
      try { localStorage.setItem(CACHE_KEY, JSON.stringify({ key: res.key, ts: Date.now() })) } catch {}
      return res.key
    }
  } catch {
    // API not configured (503) or network error — degrade gracefully
  }
  return null
}

/**
 * Clear the app-icon badge.
 *
 * The service worker sets it when a push arrives and clears it when the
 * student taps the notification. This covers the other route in: opening the
 * app directly, where the notification is still sitting unread on the lock
 * screen but the student has plainly seen whatever it was about.
 *
 * Unsupported on iOS Safari, and throws in some engines when the page is not
 * installed, so every call is guarded. A badge that will not clear is a
 * cosmetic annoyance; an exception on app focus is not.
 */
export async function clearAppBadge(): Promise<void> {
  try {
    const nav = navigator as Navigator & { clearAppBadge?: () => Promise<void> }
    if (typeof nav.clearAppBadge === 'function') {
      await nav.clearAppBadge()
      return
    }
    const reg = await navigator.serviceWorker?.getRegistration()
    const r = reg as (ServiceWorkerRegistration & { clearAppBadge?: () => Promise<void> }) | undefined
    if (r && typeof r.clearAppBadge === 'function') await r.clearAppBadge()
  } catch {
    // Badging unsupported or refused. Nothing to do and nothing to report.
  }
}
